import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import store from '@/store'
import { $t, getLanguage } from '@/lang/index'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { debounce } from 'lodash-es'

dayjs.extend(utc)
dayjs.extend(timezone)

interface AxiosResponseConfig extends AxiosResponse {
  config: AxiosRequestConfig
}

interface AxiosRequest extends AxiosRequestConfig {
  [key: string]: any
}

const state: any = store.state
const env = state.env.env
const request = axios.create({
  baseURL: '',
  timeout: 10000,
  headers: {
    zoneId: dayjs.tz.guess()
  }
})

function refreshToken() {
  return axios.create()({
    method: 'GET',
    url: `${
      process.env.NODE_ENV === 'development' ? '' : 'https://authentication.yunexpress.com'
    }/Authentication/GetAuthentication`,
    params: {
      key: state.user.userInfo.token
    }
  })
}

const debounceRefreshToken = debounce(refreshToken, 1000, {
  leading: true,
  trailing: false
})

const popErrorMessage = (message: string) => {
  ElMessage({ message, type: 'error', customClass: 'zIndex-6666' })
}

request.interceptors.request.use(
  (config: AxiosRequest) => {
    const { userInfo } = state.user
    if (userInfo) {
      if (!config.notAuth) {
        config.headers.Authorization = `Nebula token:${userInfo.token}`
        // 根据expired判断token是否过期
        if (userInfo.expired && userInfo.token) {
          const isExpired = dayjs().isAfter(dayjs(userInfo.expired))
          if (isExpired) {
            debounceRefreshToken()
              .then((res: any) => {
                const userInfo = {
                  name: res.data.info.meta.Name,
                  uid: res.data.info.uid,
                  token: res.data.token,
                  expired: res.data.expired
                }
                store.commit('user/setUserInfo', userInfo)
              })
              .catch(() => false)
          }
        }
      }
    }
    if (!(config as any).isNotAuth && !JSON.parse(localStorage.getItem('userInfo') as string)) {
      store.commit('user/redirectLogin')
    }

    const language = getLanguage()
    if (language) {
      config.headers['Accept-Language'] = language
    }

    const baseUrl = state.baseUrl.baseUrl
    // const baseUrl = 'https://services-portal-uat.yunexpress.com'
    if (config.url?.startsWith('/File') || config.url?.startsWith('http')) {
      return config
    }

    if (baseUrl) {
      config.url = `${baseUrl}${config.url}`
    } else if (env) {
      config.url = `http://${
        env.toLowerCase() === 'dev' ? 'gateway' : 'aligateway'
      }.${env.toLowerCase()}.yunexpress.com${config.url}`
    } else {
      store.commit('user/redirectLogin')
      return Promise.reject(new Error($t('main_request_tip_exist_environ')))
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

let isRefreshing = false // 控制 token 刷新状态
let requests: any[] = [] // 存储刷新 token 期间过来的 401 请求

const fileToJson = (res: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(res)
    reader.onload = (result: any) => {
      let msg = ''
      try {
        const resData = JSON.parse(result.target.result)
        if (resData) {
          msg = resData.display || resData.tips || resData.Display || resData.Tips
        }
      } catch (error) {
        console.log(error)
      }
      resolve(msg)
    }
  })
}

request.interceptors.response.use(
  (response: AxiosResponseConfig) => {
    // handleResponseIntercept(response.config)
    return response
  },
  async error => {
    if (error.config.silence) return Promise.reject(error)
    // 超出 2xx 的状态码
    const errFormat = {
      // selfCancel: false,
      message: '',
      tips: '',
      status: null
    }
    if (error.response) {
      // 请求发出收到响应，但是状态码超出 2xx 范围
      const { status, data } = error.response
      if (typeof data === 'string') {
        errFormat.message = data
      } else if (Object.prototype.toString.call(data) === '[object Object]') {
        errFormat.message =
          (data as any).Display ||
          (data as any).display ||
          (data as any).Tips ||
          (data as any).tips ||
          $t('main_request_tip_system_error')
      } else {
        const message: any = await fileToJson(data)
        errFormat.message = message
      }
      errFormat.status = status
      if (status === 401) {
        // token 无效 (没有提供 token、token 是无效的、token 过期)
        if (!state.user.userInfo) {
          ElMessage.warning($t('main_request_tip_login_expired'))
          // 没有登录
          store.commit('user/redirectLogin')
          return Promise.reject(error)
        }
        // 刷新 token
        if (!isRefreshing) {
          isRefreshing = true
          // 尝试刷新 token
          return refreshToken()
            .then((res: any) => {
              const userInfo = {
                name: res.data.info.meta.Name,
                uid: res.data.info.uid,
                token: res.data.token,
                expired: data.expired
              }
              store.commit('user/setUserInfo', userInfo)
              // 重新发送队列中的请求
              requests.forEach(cb => cb())
              // 重置 requests 数组
              requests = []
              return request(error.config)
            })
            .catch((error: any) => {
              ElMessage.warning($t('main_request_tip_login_expired'))
              store.commit('user/setUserInfo', null)
              store.commit('user/redirectLogin')
              return Promise.reject(error)
            })
            .finally(() => {
              isRefreshing = false // 重置刷新状态
            })
        }
        // 正在刷新状态下，把请求挂起放到 requests 数组中
        return new Promise(resolve => {
          requests.push(() => {
            resolve(request(error.config))
          })
        })
      } else if (status === 403) {
        popErrorMessage($t('main_request_tip_no_legal'))
      } else if (status.toString().startsWith('4')) {
        if (!error.config.isNotMessage) {
          errFormat.message
            ? popErrorMessage(errFormat.message)
            : popErrorMessage($t('main_request_tip_system_error'))
        }
      } else if (status === 504) {
        popErrorMessage($t('main_request_504_error'))
      } else if (status >= 500) {
        popErrorMessage($t('main_request_tip_server_error'))
      }
    } else if (error.request) {
      popErrorMessage($t('main_request_tip_timeout'))
    }
    return Promise.reject(errFormat)
  }
)

export default request
